import React, { useState } from "react";
import PropTypes from "prop-types";
import { Waiting, Pagination } from "components/ui";
import {
  MdVisibility,
  MdSentimentSatisfied,
  MdSentimentNeutral,
  MdSentimentDissatisfied
} from "react-icons/lib/md";
import moment from "moment";
import CallDetailsModal from "./call-details-modal";
import { formatDuration, formatScore, getScoreBadgeClass } from "./call-quality-utils";

const SentimentIcon = ({ sentiment }) => {
  if (!sentiment) return null;
  const lower = sentiment.toLowerCase();
  if (lower === "positive") {
    return <MdSentimentSatisfied className="sentiment-icon positive" />;
  }
  if (lower === "negative") {
    return <MdSentimentDissatisfied className="sentiment-icon negative" />;
  }
  return <MdSentimentNeutral className="sentiment-icon neutral" />;
};

SentimentIcon.propTypes = {
  sentiment: PropTypes.string
};

const CallsTable = ({
  calls,
  isFetching,
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange
}) => {
  const [selectedCall, setSelectedCall] = useState(null);

  return (
    <div className="calls-table-container">
      {isFetching ? (
        <Waiting />
      ) : !calls || calls.length === 0 ? (
        <div className="no-data-message">
          No call quality records found for the selected filters.
        </div>
      ) : (
        <>
          <table className="data-table calls-table">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Patient</th>
                <th>Coach</th>
                <th>Type</th>
                <th>Company</th>
                <th>Team</th>
                <th>Duration</th>
                <th>Score</th>
                <th>Disposition</th>
                <th>Patient</th>
                <th>Agent</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {calls.map((call) => (
                <tr key={call.id}>
                  <td>
                    <button
                      className="date-link"
                      onClick={() => setSelectedCall(call)}
                      title="View call details"
                    >
                      {moment(call.callDateTime).format("MM/DD/YYYY h:mm A")}
                    </button>
                  </td>
                  <td>{call.patientName || "-"}</td>
                  <td>{call.agentName || "-"}</td>
                  <td>
                    <span className={`agent-type-badge ${call.agentType?.toLowerCase()}`}>
                      {call.agentType || "-"}
                    </span>
                  </td>
                  <td>{call.companyName || "-"}</td>
                  <td>{call.teamName || "-"}</td>
                  <td>{formatDuration(call.durationSeconds)}</td>
                  <td>
                    <span className={`score-badge ${getScoreBadgeClass(call.score)}`}>
                      {formatScore(call.score)}
                    </span>
                  </td>
                  <td>{call.disposition || "-"}</td>
                  <td className="sentiment-cell">
                    <SentimentIcon sentiment={call.patientSentiment} />
                  </td>
                  <td className="sentiment-cell">
                    <SentimentIcon sentiment={call.agentSentiment} />
                  </td>
                  <td>
                    <button
                      className="view-details-btn"
                      onClick={() => setSelectedCall(call)}
                      title="View call details"
                    >
                      <MdVisibility />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalCount > 0 && totalPages > 1 && (
            <Pagination
              page={page}
              count={totalCount}
              per={pageSize}
              action={({ page: newPage }) => onPageChange(newPage)}
            />
          )}
        </>
      )}

      {selectedCall && (
        <CallDetailsModal
          call={selectedCall}
          onClose={() => setSelectedCall(null)}
        />
      )}
    </div>
  );
};

CallsTable.propTypes = {
  calls: PropTypes.array,
  isFetching: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalCount: PropTypes.number,
  totalPages: PropTypes.number,
  onPageChange: PropTypes.func
};

export default CallsTable;
